import { API_BASE_URL } from '../config';
import { parseApiError } from '../lib/apiErrorHandler';

// ========================================
// API ENDPOINTS
// Per DUAL_AUTH_IMPLEMENTATION.md specification
// ========================================

export const endpoints = {
  auth: {
    signup: '/api/auth/signup',
    login: '/api/auth/login',
    logout: '/api/auth/logout',                    // Web: POST to clear cookies
    csrf: '/api/auth/csrf',                        // Web: GET CSRF token
    refreshSession: '/api/auth/refresh-session',   // Web: POST to refresh session
    // Optional bearer refresh endpoint (some backends expose /api/auth/refresh)
    refresh: '/api/auth/refresh',
    google: '/api/auth/google',
    validate: '/api/auth/validate-token',
    profile: '/api/auth/profile',
    forgotPassword: '/api/auth/forgot-password',
    confirmEmail: '/api/auth/confirm-email',
    syncEmailVerification: '/api/auth/sync-email-verification',
    resendVerification: '/api/auth/resend-verification',
  },
  users: {
    generateToken: '/api/users/generate-token',
  },
  indianExams: {
    examsList: '/api/indian-exams/exams',
    papers: (examId) => `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers`,
    paper: (examId, paperId) => `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}`,
    paperActive: (examId, paperId) => `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}/active`,
    paperContent: (examId, paperId) => `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}/content`,
    paperContentDI: (examId, paperId) => `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers/${encodeURIComponent(paperId)}/content-di`,
    paperImages: (paperId) => `/api/indian-exams/papers/${encodeURIComponent(paperId)}/images`,
    searchPapers: '/api/indian-exams/papers/search',
    searchPapersDI: '/api/indian-exams/papers/search-di',
    ingestPreviewPaste: '/api/indian-exams/ingest/preview/paste',
    ingestPreviewUpload: '/api/indian-exams/ingest/preview/upload',
    ingestPreviewRead: (sessionId) => `/api/indian-exams/ingest/preview/${encodeURIComponent(sessionId)}`,
    // PDF Ingestion endpoints
    ingestPdfUpload: '/api/indian-exams/ingest/upload',
    ingestPdfPreview: (sessionId) => `/api/indian-exams/ingest/preview/${encodeURIComponent(sessionId)}`,
    ingestPdfUpdate: (sessionId) => `/api/indian-exams/ingest/preview/${encodeURIComponent(sessionId)}`,
    ingestPdfSave: '/api/indian-exams/ingest/save',
    ingestSessions: '/api/indian-exams/ingest/sessions',
    listPapers: (examId) => `/api/indian-exams/exams/${encodeURIComponent(examId)}/papers`,
  },
  passages: {
    create: '/api/passages',
    get: (passageId) => `/api/passages/${encodeURIComponent(passageId)}`,
    update: (passageId) => `/api/passages/${encodeURIComponent(passageId)}`,
    delete: (passageId) => `/api/passages/${encodeURIComponent(passageId)}`,
    byPaper: (paperId) => `/api/passages/paper/${encodeURIComponent(paperId)}`,
    search: '/api/passages/search',
    linkQuestion: (passageId) => `/api/passages/${encodeURIComponent(passageId)}/questions`,
    uploadImage: (passageId) => `/api/passages/${encodeURIComponent(passageId)}/image`,
    images: '/api/passages/images',
    imageById: (imageId) => `/api/passages/images/${encodeURIComponent(imageId)}`,
    imagesBulk: '/api/passages/images/bulk',
    paperImages: (paperId) => `/api/passages/paper/${encodeURIComponent(paperId)}/images`,
    deleteImage: (imageId) => `/api/passages/images/${encodeURIComponent(imageId)}`,
  },
  questionPapers: {
    generate: '/api/question-papers/generate',
    generated: '/api/question-papers/generated',
    generatedById: (paperId) => `/api/question-papers/generated/${encodeURIComponent(paperId)}`,
  },
  studyPaths: {
    list: '/api/study-paths',
    byId: (pathId) => `/api/study-paths/${encodeURIComponent(pathId)}`,
  },
};


export function url(path) {
  // If no API_BASE_URL configured, use relative path (works with Firebase Hosting rewrites)
  if (!API_BASE_URL) return path;

  // If API_BASE_URL hostname differs from current page hostname, prefer relative paths
  // so that Firebase Hosting rewrites (`/api/**`) can proxy requests to Cloud Run
  try {
    if (typeof window !== 'undefined') {
      const apiHost = new URL(API_BASE_URL).hostname;
      const pageHost = window.location.hostname;
      if (apiHost && pageHost && apiHost !== pageHost) {
        return path;
      }
    }
  } catch (e) {
    // If URL parsing fails, fall back to configured API_BASE_URL
  }

  return `${API_BASE_URL}${path}`;
}

// Helper to wrap API calls with error handling
export async function safeApiCall(fn, ...args) {
  try {
    return await fn(...args);
  } catch (error) {
    throw new Error(await parseApiError(error));
  }
}
